import { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { Link } from 'react-router-dom';
import { Bot, Sparkles, BarChart3, ArrowRight } from 'lucide-react';

const capabilities = [
  {
    icon: Bot,
    title: 'AI-Powered Chatbot',
    desc: 'Asisten digital 24/7 yang memahami konteks bisnis Anda. Bukan bot generik—chatbot yang bicara dengan suara brand Anda.',
  },
  {
    icon: Sparkles,
    title: 'Smart Content',
    desc: 'Konten yang beradaptasi dengan perilaku pengunjung. Personalisasi yang meningkatkan engagement dan konversi.',
  },
  {
    icon: BarChart3,
    title: 'Predictive Analytics',
    desc: 'Data yang menceritakan masa depan bisnis Anda. Keputusan berbasis insight, bukan intuisi.',
  },
];

const chatDemo = [
  { role: 'user', text: 'Bagaimana cara meningkatkan konversi website saya?' },
  { role: 'bot', text: 'Berdasarkan analisis traffic Anda, saya melihat 68% pengunjung keluar di halaman produk. Mari kita optimasi copywriting dan CTA placement di sana.' },
  { role: 'user', text: 'Bisakah kamu membuat rekomendasi produk otomatis?' },
  { role: 'bot', text: 'Tentu! Saya bisa menganalisis pola pembelian dan browsing history untuk menampilkan rekomendasi personal. Ini biasanya meningkatkan cross-sell sebesar 23%.' },
];

export default function AIShowcase() {
  const { theme } = useTheme();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [visibleMessages, setVisibleMessages] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const timer = setInterval(() => {
      setVisibleMessages(prev => {
        if (prev >= chatDemo.length) {
          clearInterval(timer);
          return prev;
        }
        return prev + 1;
      });
    }, 1200);
    return () => clearInterval(timer);
  }, [inView]);

  return (
    <section
      data-testid="ai-showcase-section"
      ref={ref}
      style={{ backgroundColor: theme === 'dark' ? '#0C1120' : '#F0EDE8' }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32 lg:py-40">
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
        >
          <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color: 'var(--accent-primary)' }}>
            AI Capabilities
          </span>
        </motion.div>

        <motion.h2
          className="font-heading text-3xl md:text-5xl font-semibold tracking-tight leading-tight max-w-4xl mb-16"
          style={{ color: 'var(--text-primary)' }}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1 }}
        >
          Kami tidak hanya membangun website.{' '}
          <span style={{ color: 'var(--accent-primary)' }}>Kami mengajarkannya berpikir.</span>
        </motion.h2>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left — Cards */}
          <div className="space-y-6">
            {capabilities.map((cap, i) => {
              const Icon = cap.icon;
              return (
                <motion.div
                  key={i}
                  className="glass-card p-6 flex gap-5 group"
                  initial={{ opacity: 0, x: -30 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.2 + i * 0.15 }}
                  data-testid={`ai-card-${i}`}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                    style={{
                      background: theme === 'dark' ? 'rgba(0,255,133,0.08)' : 'rgba(13,148,136,0.08)',
                      color: 'var(--accent-primary)',
                    }}
                  >
                    <Icon size={22} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                      {cap.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                      {cap.desc}
                    </p>
                  </div>
                </motion.div>
              );
            })}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.7 }}
            >
              <Link
                to="/tanya-bewhy"
                data-testid="ai-cta-chatbot"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-heading font-semibold text-sm tracking-wide uppercase transition-all duration-300 hover:-translate-y-1"
                style={{
                  backgroundColor: 'var(--accent-primary)',
                  color: theme === 'dark' ? '#0D0D0D' : '#FFFFFF',
                  boxShadow: `0 0 30px ${theme === 'dark' ? 'rgba(0,255,133,0.2)' : 'rgba(13,148,136,0.2)'}`,
                }}
              >
                Lihat AI kami beraksi
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>

          {/* Right — Live Demo Mini */}
          <motion.div
            className="glass-card p-6 lg:p-8"
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 }}
            data-testid="ai-demo-chat"
          >
            <div className="flex items-center gap-3 mb-6 pb-4" style={{ borderBottom: '1px solid var(--border-card)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-primary)', color: theme === 'dark' ? '#0D0D0D' : '#FFFFFF' }}>
                <Bot size={16} />
              </div>
              <div>
                <span className="font-heading text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Bewhy AI</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  <span className="text-[10px] font-mono" style={{ color: 'var(--text-secondary)' }}>Online</span>
                </div>
              </div>
            </div>

            <div className="space-y-4 min-h-[280px]">
              {chatDemo.slice(0, visibleMessages).map((msg, i) => (
                <motion.div
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <div
                    className="max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed"
                    style={{
                      backgroundColor: msg.role === 'user'
                        ? (theme === 'dark' ? 'rgba(58,130,255,0.15)' : 'rgba(37,99,235,0.1)')
                        : (theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
                      color: 'var(--text-primary)',
                      borderBottomRightRadius: msg.role === 'user' ? '4px' : '16px',
                      borderBottomLeftRadius: msg.role === 'bot' ? '4px' : '16px',
                    }}
                  >
                    {msg.text}
                  </div>
                </motion.div>
              ))}

              {visibleMessages < chatDemo.length && visibleMessages > 0 && (
                <div className="flex justify-start">
                  <div className="flex gap-1 px-4 py-3">
                    {[0, 1, 2].map(j => (
                      <motion.div
                        key={j}
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: 'var(--text-secondary)' }}
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ repeat: Infinity, duration: 1, delay: j * 0.2 }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
